import { Button, Typography } from '@mui/material';
import { getIn, useField, FieldArray } from 'formik';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSensor,
  type Over,
  DndContext,
  useSensors,
  DragOverlay,
  type Active,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  type DragEndEvent,
  type DragStartEvent,
  type CollisionDetection,
} from '@dnd-kit/core';

import { useAlert } from 'hooks';
import { AddButton } from 'components';
import { fieldTypeTitle, getDefaultBlock } from 'utils/form';
import { FormBlock, FormFieldType, FormFieldTypeTitle } from 'types';

import { FormViewProps } from '../types';

import { BlockItem } from './block-item';
import { DragPreviewCard } from './drag-preview-card';
import { SharedFieldDnDContext } from './dnd-context';
import {
  isFieldItemId,
  isBlockItemId,
  reorderBlocks,
  getBlockItemId,
  getFieldItemId,
  moveFieldBetweenContainers,
} from './dnd';
import {
  cloneSectionForPaste,
  readBuilderClipboard,
  buildClipboardPayload,
  writeBuilderClipboard,
  createOutcomeSourceKeys,
  findFirstIncompatibleFieldType,
} from './clipboard';

const getContainerId = (entry: Over | Active | null): string | null => {
  const containerId = entry?.data.current?.containerId;
  return typeof containerId === 'string' ? containerId : null;
};

const EMPTY_OUTCOME_FIELD_SOURCES: FormViewProps['outcomeFieldSources'] = [];

export const FormBuilder = ({
  blocksName,
  allowInputId = false,
  allowOutcomes = false,
  allowOutcomeFields = false,
  allowReferenceCodes = false,
  outcomeFieldSources = EMPTY_OUTCOME_FIELD_SOURCES,
}: FormViewProps) => {
  const { showErrorAlert, showSuccessAlert } = useAlert();
  const [, { value: blocks }, { setValue: setBlocksValue }] =
    useField<FormBlock[]>(blocksName);
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [activeFieldWidth, setActiveFieldWidth] = useState<number | null>(null);
  const [activeBlockItemId, setActiveBlockItemId] = useState<string | null>(
    null
  );
  const [activeBlockWidth, setActiveBlockWidth] = useState<number | null>(null);
  const isPastingSectionRef = useRef(false);
  const [expandedBlockIds, setExpandedBlockIds] = useState<string[]>(() =>
    blocks.map((block) => block.id)
  );
  const [isReorderMode, setIsReorderMode] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const topLevelCompatibilityRules = useMemo(
    () => ({
      allowOutcomes,
      allowOutcomeFields,
      allowPageBreak: true,
      allowFieldArray: true,
    }),
    [allowOutcomes, allowOutcomeFields]
  );
  const outcomeSourceKeys = useMemo(
    () => createOutcomeSourceKeys(outcomeFieldSources),
    [outcomeFieldSources]
  );

  const canDeleteBlocks = blocks.length > 1;

  const blockIds = useMemo(() => blocks.map((b) => b.id).join(','), [blocks]);
  const blockIdList = useMemo(
    () => blockIds.split(',').filter(Boolean),
    [blockIds]
  );
  const blockSortableIds = useMemo(
    () => blockIdList.map((id) => getBlockItemId(id)),
    [blockIdList]
  );
  const fieldContainerIds = useMemo(
    () => blockIdList.map((_, index) => `${blocksName}.${index}.fields`),
    [blockIdList, blocksName]
  );

  useEffect(() => {
    setExpandedBlockIds((currentExpandedIds) => {
      const nextIds = new Set(blockIds.split(',').filter(Boolean));
      return currentExpandedIds.filter((id) => nextIds.has(id));
    });
  }, [blockIds]);

  const handleBlocksDragStart = useCallback(({ active }: DragStartEvent) => {
    setActiveBlockItemId(typeof active.id === 'string' ? active.id : null);
    setActiveBlockWidth(active.rect.current.initial?.width ?? null);
  }, []);

  const clearBlockDragState = useCallback(() => {
    setActiveBlockItemId(null);
    setActiveBlockWidth(null);
  }, []);

  const clearFieldDragState = useCallback(() => {
    setActiveFieldId(null);
    setActiveFieldWidth(null);
  }, []);

  const handleBlocksDragEnd = useCallback(
    ({ over, active }: DragEndEvent) => {
      clearBlockDragState();

      if (
        !over ||
        typeof active.id !== 'string' ||
        typeof over.id !== 'string' ||
        active.id === over.id ||
        !isBlockItemId(active.id) ||
        !isBlockItemId(over.id)
      ) {
        return;
      }

      const nextValues = reorderBlocks(
        { [blocksName]: blocks },
        blocksName,
        active.id,
        over.id
      );
      const nextBlocks = (getIn(nextValues, blocksName) ?? []) as FormBlock[];

      setBlocksValue(nextBlocks);
    },
    [blocks, blocksName, clearBlockDragState, setBlocksValue]
  );

  const handleFieldDragStart = useCallback(({ active }: DragStartEvent) => {
    if (
      typeof active.id !== 'string' ||
      !isFieldItemId(active.id) ||
      typeof active.data.current?.sortable?.containerId !== 'string' ||
      typeof active.data.current?.sortable?.index !== 'number'
    ) {
      return;
    }

    setActiveFieldId(active.id);
    setActiveFieldWidth(active.rect.current.initial?.width ?? null);
  }, []);

  const handleFieldDragEnd = useCallback(
    ({ over, active }: DragEndEvent) => {
      clearFieldDragState();

      if (
        !over ||
        typeof active.id !== 'string' ||
        typeof over.id !== 'string' ||
        active.id === over.id
      ) {
        return;
      }

      const sourcePath = getContainerId(active);
      const targetPath = getContainerId(over);

      if (!sourcePath || !targetPath) {
        return;
      }

      const nextValues = moveFieldBetweenContainers({
        sourcePath,
        targetPath,
        overItemId: over.id,
        activeItemId: active.id,
        values: { [blocksName]: blocks },
      });
      const nextBlocks = (getIn(nextValues, blocksName) ?? []) as FormBlock[];

      setBlocksValue(nextBlocks);
    },
    [blocks, blocksName, clearFieldDragState, setBlocksValue]
  );

  const activeBlock = activeBlockItemId
    ? blocks.find((b) => getBlockItemId(b.id) === activeBlockItemId)
    : undefined;
  const activeField = useMemo(
    () =>
      activeFieldId
        ? blocks
            .flatMap((b) => b.fields)
            .find((f) => getFieldItemId(f.id) === activeFieldId)
        : undefined,
    [activeFieldId, blocks]
  );
  const sharedDnDContextValue = useMemo(
    () => ({
      activeFieldId,
      containerIds: fieldContainerIds,
    }),
    [activeFieldId, fieldContainerIds]
  );
  const collisionDetection = useCallback<CollisionDetection>(
    (args) => {
      const activeId = String(args.active.id);
      const filteredDroppableContainers = args.droppableContainers.filter(
        (container) => {
          const containerId = String(container.id);

          if (isFieldItemId(activeId)) {
            return (
              isFieldItemId(containerId) ||
              fieldContainerIds.includes(containerId)
            );
          }

          return isBlockItemId(containerId);
        }
      );

      return closestCorners({
        ...args,
        droppableContainers:
          filteredDroppableContainers.length > 0
            ? filteredDroppableContainers
            : args.droppableContainers,
      });
    },
    [fieldContainerIds]
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      if (
        typeof event.active.id === 'string' &&
        isFieldItemId(event.active.id)
      ) {
        handleFieldDragStart(event);
        return;
      }

      handleBlocksDragStart(event);
    },
    [handleBlocksDragStart, handleFieldDragStart]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (
        typeof event.active.id === 'string' &&
        isFieldItemId(event.active.id)
      ) {
        handleFieldDragEnd(event);
        return;
      }

      handleBlocksDragEnd(event);
    },
    [handleBlocksDragEnd, handleFieldDragEnd]
  );

  const handleDragCancel = useCallback(() => {
    clearBlockDragState();
    clearFieldDragState();
  }, [clearBlockDragState, clearFieldDragState]);
  const handleToggleBlock = useCallback(
    (blockId: string, expanded: boolean) => {
      setExpandedBlockIds((currentExpandedIds) =>
        expanded
          ? currentExpandedIds.includes(blockId)
            ? currentExpandedIds
            : [...currentExpandedIds, blockId]
          : currentExpandedIds.filter((id) => id !== blockId)
      );
    },
    []
  );
  const handleCopySection = useCallback(
    async (index: number) => {
      const sourceBlock = blocks[index];
      if (!sourceBlock) {
        showErrorAlert('Section does not exist');
        return;
      }

      try {
        await writeBuilderClipboard(
          buildClipboardPayload('section', sourceBlock, {
            sectionExpanded: expandedBlockIds.includes(sourceBlock.id),
          })
        );
        showSuccessAlert('Section copied');
      } catch (error) {
        showErrorAlert(
          error instanceof Error ? error.message : 'Failed to copy section'
        );
      }
    },
    [blocks, expandedBlockIds, showErrorAlert, showSuccessAlert]
  );
  const handlePasteSectionAfter = useCallback(
    async (index: number) => {
      if (isPastingSectionRef.current) return;
      isPastingSectionRef.current = true;
      try {
        const payload = await readBuilderClipboard();
        if (payload.entityType !== 'section') {
          showErrorAlert('Clipboard does not contain a section');
          return;
        }

        const sourceSection = payload.data as FormBlock;
        const incompatibleType = sourceSection.fields
          .map((field) =>
            findFirstIncompatibleFieldType(field, topLevelCompatibilityRules)
          )
          .find((type): type is NonNullable<typeof type> => Boolean(type));

        if (incompatibleType) {
          showErrorAlert(
            `Cannot paste section with unsupported type "${fieldTypeTitle(
              incompatibleType
            )}"`
          );
          return;
        }

        const nextBlocks = [...blocks];
        const pastedSection = cloneSectionForPaste(
          sourceSection,
          outcomeSourceKeys
        );
        nextBlocks.splice(index + 1, 0, pastedSection);
        setBlocksValue(nextBlocks);
        const shouldBeExpanded = payload.metadata?.sectionExpanded ?? true;
        setExpandedBlockIds((currentExpandedIds) => {
          if (shouldBeExpanded) {
            return currentExpandedIds.includes(pastedSection.id)
              ? currentExpandedIds
              : [...currentExpandedIds, pastedSection.id];
          }

          return currentExpandedIds.filter((id) => id !== pastedSection.id);
        });
        showSuccessAlert('Section pasted');
      } catch (error) {
        showErrorAlert(
          error instanceof Error ? error.message : 'Failed to paste section'
        );
      } finally {
        isPastingSectionRef.current = false;
      }
    },
    [
      blocks,
      setBlocksValue,
      showErrorAlert,
      showSuccessAlert,
      outcomeSourceKeys,
      topLevelCompatibilityRules,
    ]
  );
  const content = ({
    push,
    remove,
  }: {
    remove: (index: number) => void;
    push: (value: FormBlock) => void;
  }) => {
    const blockItems = blockIdList.map((blockId, index) => (
      <BlockItem
        key={blockId}
        index={index}
        blockId={blockId}
        onRemove={remove}
        blocksName={blocksName}
        simplified={isReorderMode}
        allowInputId={allowInputId}
        onToggle={handleToggleBlock}
        allowOutcomes={allowOutcomes}
        enableSorting={isReorderMode}
        onCopySection={handleCopySection}
        canDeleteBlocks={canDeleteBlocks}
        allowOutcomeFields={allowOutcomeFields}
        allowReferenceCodes={allowReferenceCodes}
        outcomeFieldSources={outcomeFieldSources}
        onPasteSectionAfter={handlePasteSectionAfter}
        expanded={expandedBlockIds.includes(blockId)}
      />
    ));

    return (
      <SharedFieldDnDContext.Provider value={sharedDnDContextValue}>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
            <Typography variant="subtitle2">
              {isReorderMode ? 'Reorder mode' : 'Edit mode'}
            </Typography>
            <Button
              size="small"
              variant={isReorderMode ? 'outlined' : 'contained'}
              onClick={() => setIsReorderMode((currentValue) => !currentValue)}
            >
              {isReorderMode ? 'Switch to edit' : 'Switch to reorder'}
            </Button>
          </div>

          {isReorderMode ? (
            <SortableContext
              items={blockSortableIds}
              strategy={verticalListSortingStrategy}
            >
              {blockItems}
            </SortableContext>
          ) : (
            blockItems
          )}

          {!isReorderMode && (
            <AddButton
              title="section"
              onClick={() => {
                const nextBlock = getDefaultBlock();

                push(nextBlock);
                setExpandedBlockIds((currentExpandedIds) => [
                  ...currentExpandedIds,
                  nextBlock.id,
                ]);
              }}
            />
          )}
        </div>

        {isReorderMode && (
          <DragOverlay>
            {activeField ? (
              <DragPreviewCard
                showHandle
                width={activeFieldWidth}
                title={FormFieldTypeTitle[activeField.type]}
                subtitle={
                  activeField.label?.trim() ||
                  (activeField.type === FormFieldType.FIELD_ARRAY
                    ? undefined
                    : 'Untitled field')
                }
              />
            ) : activeBlock ? (
              <DragPreviewCard
                width={activeBlockWidth}
                title={activeBlock.name?.trim() || 'Untitled section'}
              />
            ) : null}
          </DragOverlay>
        )}
      </SharedFieldDnDContext.Provider>
    );
  };

  return (
    <FieldArray
      name={blocksName}
      render={({ push, remove }) =>
        isReorderMode ? (
          <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            collisionDetection={collisionDetection}
          >
            {content({ push, remove })}
          </DndContext>
        ) : (
          content({ push, remove })
        )
      }
    />
  );
};
